import type { FC, PropsWithChildren } from 'react'
import { lazy, useMemo, Suspense } from 'react'
import { cn } from '@/utils'
import type { CueTimingDurationPopoverProps } from '../model/props'

const Popover = lazy(() =>
  import('../../../components/interactives/Popover.jsx').then((m) => ({
    default: m.Popover,
  })),
)
const PopoverContent = lazy(() =>
  import('../../../components/interactives/Popover.jsx').then((m) => ({
    default: m.PopoverContent,
  })),
)
const PopoverTrigger = lazy(() =>
  import('../../../components/interactives/Popover.jsx').then((m) => ({
    default: m.PopoverTrigger,
  })),
)

export const CueTimingDurationPopover: FC<
  PropsWithChildren<CueTimingDurationPopoverProps>
> = ({
  open,
  setOpen,
  buttonLabel,
  buttonClassName,
  onSave,
  onCancel,
  readonly,
  children,
}) => {
  const TriggerButton = useMemo(() => {
    return (
      <button
        type="button"
        disabled={readonly}
        onClick={() => {
          if (readonly) return
          setOpen(true)
        }}
        className={cn(
          'max-h-full h-6 min-h-max px-1.5 w-full min-w-12 rounded-sm bg-black/50 hover:enabled:bg-gray-600/40',
          'disabled:pointer-events-none',
          open && 'ring-1 ring-white',
        )}
      >
        <span className={buttonClassName}>{buttonLabel}</span>
      </button>
    )
  }, [buttonClassName, buttonLabel, open, readonly, setOpen])

  // Performance: only mount the Popover tree when open, avoiding hundreds of
  // idle useFloating hooks on large rundowns. Matches CueTimingStartTimePopover.
  if (!open) return TriggerButton

  // When open, mount the Popover lazily and control it via `open` state
  return (
    <Suspense fallback={TriggerButton}>
      <Popover
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild={true} disabled={readonly}>
          {TriggerButton}
        </PopoverTrigger>
        <PopoverContent
          onSave={onSave}
          onCancel={onCancel}
          saveDisabled={readonly}
        >
          {children}
        </PopoverContent>
      </Popover>
    </Suspense>
  )
}
